import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
  AccessGroup,
  AccessGroupRule,
  EntryRule,
  EventsRule,
  FlatGateRule,
  GateGroup,
  GateRule,
  liftFlatGateRule,
  PriceCodeRule,
  RuleItemRef,
  ScanPeriodRule,
  SectionRowRule,
  TicketUsageRule
} from '../../../../models/scan-testing.model';

// An event offered to the config modals, along with the plans it contains.
export interface RuleEvent {
  eventName: string;
  description: string;
  plans: string[];
}

// Locator fields pre-filled onto an extended seed row generated from a rule.
export interface SeedLocatorHint {
  section?: string;
  row?: string;
  priceCode?: string;
  ticketUsageLabel?: string;
}

// One extended-test row the parent should seed for a Section & Row / Ticket
// Usage / Price Code rule. Keyed by the rule's seedKey plus its locator.
export interface SeedRowRequest {
  gate: string;
  ruleType: 'section_row' | 'ticket_usage' | 'price_code';
  seedKey: string;
  locatorHint: SeedLocatorHint;
}

// Emitted on save: the persisted gate rules plus the seed rows to create.
export interface RuleSavePayload {
  rules: GateRule[];
  seedRequests: SeedRowRequest[];
}

// A rule as it sits in the draft editor, with a client-side id for trackBy.
interface DraftRule {
  id: string;
  rule: EntryRule;
}

// A group of rules that combine with AND ("+"). A gate holds one or more
// groups; groups combine with OR. Each group is its own drag-drop target.
interface DraftGroup {
  id: string;
  rules: DraftRule[];
}

interface DraftGate {
  gate: string;
  groups: DraftGroup[];
  collapsed: boolean;
}

// A rule-type chip just dropped into a group, awaiting configuration in a modal.
interface PendingRuleDrop {
  group: DraftGroup;
  ruleType: EntryRule['type'];
}

// An existing rule opened for editing in its config modal.
interface ActiveRuleEdit {
  group: DraftGroup;
  draftRule: DraftRule;
}

// Every rule type shown in the palette, in display order.
const RULE_TYPES: EntryRule['type'][] = [
  'scan_period',
  'events',
  'access_group',
  'section_row',
  'ticket_usage',
  'price_code'
];

const RULE_TYPE_LABELS: Record<EntryRule['type'], string> = {
  scan_period: 'Scan Period',
  events: 'Events',
  access_group: 'Access Group',
  section_row: 'Section & Row',
  ticket_usage: 'Ticket Usage',
  price_code: 'Price Code'
};

@Component({
  selector: 'app-set-rules',
  templateUrl: './set-rules.component.html',
  standalone: false,
  styleUrls: ['./set-rules.component.scss']
})
export class SetRulesComponent implements OnChanges {
  @Input() gateNames: string[] = [];
  @Input() events: RuleEvent[] = [];
  @Input() existingRules: GateRule[] = [];
  @Input() gateGroups: GateGroup[] = [];
  @Input() accessGroups: AccessGroup[] = [];
  @Input() priceCodes: string[] = [];
  @Input() saving = false;
  @Input() error = '';

  @Output() save = new EventEmitter<RuleSavePayload>();
  @Output() cancel = new EventEmitter<void>();
  @Output() saveAccessGroups = new EventEmitter<AccessGroup[]>();

  readonly ruleTypes = RULE_TYPES;

  gates: DraftGate[] = [];

  // Config-modal state. `pendingDrop` is set when a fresh chip is dropped;
  // `activeEdit` when an existing rule is reopened. At most one is non-null.
  pendingDrop: PendingRuleDrop | null = null;
  activeEdit: ActiveRuleEdit | null = null;
  showAccessGroupManager = false;

  private ruleSeq = 0;
  private groupSeq = 0;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['gateNames'] || changes['existingRules']) {
      this.rebuildGates();
    }
  }

  private rebuildGates(): void {
    // Accept both the grouped shape and the pre-grouping flat shape, lifting the
    // latter into a single AND group so older saved data still loads.
    const existingByGate = new Map<string, GateRule>();
    for (const rule of (this.existingRules || []) as Array<GateRule | FlatGateRule>) {
      const grouped = Array.isArray((rule as GateRule).groups)
        ? (rule as GateRule)
        : liftFlatGateRule(rule as FlatGateRule);
      existingByGate.set(grouped.gate, grouped);
    }

    // Preserve collapsed state across rebuilds so editing doesn't reset the view.
    const collapsedByGate = new Map<string, boolean>();
    for (const gate of this.gates) {
      collapsedByGate.set(gate.gate, gate.collapsed);
    }

    this.gates = this.gateNames.map((gate) => {
      const existing = existingByGate.get(gate);
      const groups = (existing?.groups || []).map((rules) => this.toDraftGroup(rules));
      // Always show at least one (empty) group so there's a drop target.
      if (!groups.length) { groups.push({ id: this.newGroupId(), rules: [] }); }
      return { gate, groups, collapsed: collapsedByGate.get(gate) ?? false };
    });
  }

  private toDraftGroup(rules: EntryRule[]): DraftGroup {
    return { id: this.newGroupId(), rules: (rules || []).map((rule) => this.toDraftRule(rule)) };
  }

  private toDraftRule(rule: EntryRule): DraftRule {
    return { id: this.newDraftRuleId(), rule: { ...rule } as EntryRule };
  }

  private newDraftRuleId(): string {
    this.ruleSeq += 1;
    return `dr_${this.ruleSeq}`;
  }

  private newGroupId(): string {
    this.groupSeq += 1;
    return `grp_${this.groupSeq}`;
  }

  /* Collapse / expand --------------------------------------------------- */

  toggleGate(gate: DraftGate): void {
    gate.collapsed = !gate.collapsed;
  }

  collapseAll(collapsed: boolean): void {
    for (const gate of this.gates) {
      gate.collapsed = collapsed;
    }
  }

  /* Palette chip drag --------------------------------------------------- */

  ruleTypeLabel(type: EntryRule['type']): string {
    return RULE_TYPE_LABELS[type];
  }

  // A short glyph shown on each palette chip (kept ASCII to avoid icon deps).
  ruleTypeIcon(type: EntryRule['type']): string {
    switch (type) {
      case 'scan_period': return '⏱';
      case 'events': return '🎫';
      case 'access_group': return '👥';
      case 'section_row': return '🎟';
      case 'ticket_usage': return '🔁';
      case 'price_code': return '＄';
    }
  }

  // Dropping a rule-type chip into a group opens that type's config modal. The
  // rule is only added once the modal is confirmed (see commitRule).
  onDropRuleType(event: CdkDragDrop<DraftRule[]>, group: DraftGroup): void {
    if (this.saving) { return; }
    const ruleType = event.item.data as EntryRule['type'];
    if (!ruleType) { return; }
    this.activeEdit = null;
    this.pendingDrop = { group, ruleType };
  }

  /* Group management ---------------------------------------------------- */

  // Adds a new empty OR group to a gate. Rules dropped into it combine with the
  // other groups by OR (any group accepting a barcode accepts it at the gate).
  addGroup(gate: DraftGate): void {
    if (this.saving) { return; }
    gate.groups = [...gate.groups, { id: this.newGroupId(), rules: [] }];
  }

  removeGroup(gate: DraftGate, group: DraftGroup): void {
    if (this.saving) { return; }
    gate.groups = gate.groups.filter((g) => g.id !== group.id);
  }

  /* Rule editing / removal ---------------------------------------------- */

  editRule(group: DraftGroup, draftRule: DraftRule): void {
    if (this.saving) { return; }
    this.pendingDrop = null;
    this.activeEdit = { group, draftRule };
  }

  // Removes a rule; if that empties a group and the gate has other groups, the
  // now-empty group is dropped too so no stray empty OR box lingers.
  removeRule(gate: DraftGate, group: DraftGroup, draftRule: DraftRule): void {
    if (this.saving) { return; }
    group.rules = group.rules.filter((r) => r.id !== draftRule.id);
    if (!group.rules.length && gate.groups.length > 1) {
      gate.groups = gate.groups.filter((g) => g.id !== group.id);
    }
  }

  clearGate(gate: DraftGate): void {
    if (this.saving || !this.gateHasRules(gate)) { return; }
    gate.groups = [{ id: this.newGroupId(), rules: [] }];
  }

  // Whether a gate has at least one rule in any group.
  gateHasRules(gate: DraftGate): boolean {
    return gate.groups.some((g) => g.rules.length > 0);
  }

  // Total rule count across all of a gate's groups (for the collapsed summary).
  gateRuleCount(gate: DraftGate): number {
    return gate.groups.reduce((sum, g) => sum + g.rules.length, 0);
  }

  /* Config-modal commit / cancel ---------------------------------------- */

  // Commits a configured rule: replaces the rule under edit, or appends a new
  // draft rule for a pending drop. The typed confirm* wrappers keep the
  // template bindings strongly typed per modal.
  private commitRule(rule: EntryRule): void {
    if (this.activeEdit) {
      this.activeEdit.draftRule.rule = rule;
      this.activeEdit = null;
      return;
    }
    if (!this.pendingDrop) { return; }
    const draftRule: DraftRule = { id: this.newDraftRuleId(), rule };
    this.pendingDrop.group.rules = [...this.pendingDrop.group.rules, draftRule];
    this.pendingDrop = null;
  }

  confirmScanPeriod(rule: ScanPeriodRule): void { this.commitRule(rule); }
  confirmEventsRule(rule: EntryRule): void { this.commitRule(rule); }
  confirmAccessGroupRule(rule: EntryRule): void { this.commitRule(rule); }
  confirmSectionRowRule(rule: EntryRule): void { this.commitRule(rule); }
  confirmTicketUsageRule(rule: EntryRule): void { this.commitRule(rule); }
  confirmPriceCodeRule(rule: EntryRule): void { this.commitRule(rule); }

  cancelRuleModal(): void {
    this.pendingDrop = null;
    this.activeEdit = null;
  }

  // Whether a config modal of the given type should be open — either from a
  // fresh drop of that type or an active edit of a rule of that type.
  isModalOpen(type: EntryRule['type']): boolean {
    return this.pendingDrop?.ruleType === type || !!this.editingOfType(type);
  }

  // The rule under edit, if its type matches — narrowed for each modal's input.
  private editingOfType<T extends EntryRule['type']>(type: T): Extract<EntryRule, { type: T }> | undefined {
    const rule = this.activeEdit?.draftRule.rule;
    return rule && rule.type === type ? (rule as Extract<EntryRule, { type: T }>) : undefined;
  }

  editingScanPeriod(): ScanPeriodRule | undefined { return this.editingOfType('scan_period'); }
  editingEvents(): EventsRule | undefined { return this.editingOfType('events'); }
  editingAccessGroup(): AccessGroupRule | undefined { return this.editingOfType('access_group'); }
  editingSectionRow(): SectionRowRule | undefined { return this.editingOfType('section_row'); }
  editingTicketUsage(): TicketUsageRule | undefined { return this.editingOfType('ticket_usage'); }
  editingPriceCode(): PriceCodeRule | undefined { return this.editingOfType('price_code'); }

  /* Access group manager ------------------------------------------------ */

  openAccessGroupManager(): void {
    if (this.saving) { return; }
    this.showAccessGroupManager = true;
  }

  closeAccessGroupManager(): void {
    this.showAccessGroupManager = false;
  }

  onSaveAccessGroups(groups: AccessGroup[]): void {
    this.showAccessGroupManager = false;
    this.saveAccessGroups.emit(groups);
  }

  /* Accept-all / copy to group ------------------------------------------ */

  // Fills the gate's first group with a single Events rule permitting every
  // event and plan. Replaces an existing Events rule in that group if present.
  // Creates the first group when the gate has none. No-op when there are no events.
  acceptAllEvents(gate: DraftGate): void {
    if (this.saving || !this.events.length) { return; }
    const items: RuleItemRef[] = [];
    for (const ev of this.events) {
      items.push({ kind: 'event', eventName: ev.eventName });
      for (const plan of ev.plans) {
        items.push({ kind: 'plan', eventName: ev.eventName, plan });
      }
    }
    if (!gate.groups.length) {
      gate.groups = [{ id: this.newGroupId(), rules: [] }];
    }
    const group = gate.groups[0];
    const existing = group.rules.find((dr) => dr.rule.type === 'events');
    if (existing) {
      (existing.rule as EventsRule).items = items;
    } else {
      group.rules = [...group.rules, { id: this.newDraftRuleId(), rule: { type: 'events', items } }];
    }
  }

  // The group this gate belongs to, if it shares one with at least one other
  // gate configured for this performance. Returns null otherwise.
  gateGroupFor(gate: DraftGate): GateGroup | null {
    for (const group of this.gateGroups || []) {
      const members = (group.gateNames || []).filter((name) => this.gateNames.includes(name));
      if (members.length >= 2 && members.includes(gate.gate)) { return group; }
    }
    return null;
  }

  // Copies this gate's rules onto every other gate in its group, replacing
  // whatever those gates held. Seed keys are preserved so re-save stays idempotent.
  copyToGroup(gate: DraftGate): void {
    if (this.saving) { return; }
    const group = this.gateGroupFor(gate);
    if (!group) { return; }

    const targets = new Set(group.gateNames);
    for (const other of this.gates) {
      if (other.gate === gate.gate || !targets.has(other.gate)) { continue; }
      other.groups = gate.groups.map((grp) => ({
        id: this.newGroupId(),
        rules: grp.rules.map((dr) => ({
          id: this.newDraftRuleId(),
          rule: { ...dr.rule } as EntryRule
        }))
      }));
    }
  }

  /* Summaries ----------------------------------------------------------- */

  ruleSummary(rule: EntryRule): string {
    switch (rule.type) {
      case 'scan_period':
        return this.scanPeriodSummary(rule);
      case 'events':
        return this.eventsSummary(rule.items);
      case 'access_group': {
        const group = (this.accessGroups || []).find((g) => g.id === rule.groupId);
        if (!group) { return `Access Group: ${rule.groupId}`; }
        const preview = this.accessGroupPreview(group);
        return preview ? `${group.name}: ${preview}` : group.name;
      }
      case 'section_row':
        return this.sectionRowSummary(rule.selections);
      case 'ticket_usage':
        return `${rule.dayUsage === 'single' ? '1 Day' : 'Multi-Day'}; `
          + `${rule.entryUsage === 'one_time' ? 'One-time' : 'Unlimited'}`;
      case 'price_code':
        return this.chosenPreview(rule.priceCodes, 'No price codes');
    }
  }

  // "First, Second, Third +N more" from a list of chosen values — the shared
  // shape used to show what an Events / Section&Row / Price Code rule contains.
  private chosenPreview(values: string[], emptyText: string): string {
    const MAX = 3;
    if (!values.length) { return emptyText; }
    const shown = values.slice(0, MAX).join(', ');
    const extra = values.length - MAX;
    return extra > 0 ? `${shown} +${extra} more` : shown;
  }

  // A preview of an Events rule's chosen events/plans (whole events shown by
  // name; individual plans as "Event · Plan").
  private eventsSummary(items: RuleItemRef[]): string {
    const names = (items || []).map((ref) =>
      ref.kind === 'plan' ? `${ref.eventName} · ${ref.plan}` : ref.eventName);
    return this.chosenPreview(names, 'No events');
  }

  // A preview of the chosen sections/rows, e.g. "101 (AA, BB), 102 (whole)".
  private sectionRowSummary(selections: Array<{ section: string; rows: string[] }>): string {
    const labels = (selections || []).map((sel) => {
      const rows = (sel.rows || []).filter((r) => r.trim());
      return rows.length ? `${sel.section} (${rows.join(', ')})` : `${sel.section} (whole)`;
    });
    return this.chosenPreview(labels, 'No sections');
  }

  // A short, human-readable preview of an access group's contents for the rule
  // card — the first few event/plan names, with a "+N more" tail if truncated.
  private accessGroupPreview(group: AccessGroup): string {
    const names = (group.items || []).map((ref) =>
      ref.kind === 'plan' ? `${ref.eventName} · ${ref.plan}` : ref.eventName);
    return names.length ? this.chosenPreview(names, '') : '';
  }

  private scanPeriodSummary(rule: ScanPeriodRule): string {
    if (rule.mode === 'offset') {
      return `Scan ${this.offsetLabel(rule.openValue)} → ${this.offsetLabel(rule.closeValue)}`;
    }
    return `Scan ${this.clockLabel(rule.openValue)} → ${this.clockLabel(rule.closeValue)}`;
  }

  // Renders a minutes-from-EP offset as e.g. "-12h 00m" / "+1h 30m".
  private offsetLabel(mins: number): string {
    const sign = mins < 0 ? '-' : '+';
    const abs = Math.abs(mins);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    return `${sign}${h}h ${m.toString().padStart(2, '0')}m`;
  }

  private clockLabel(epochSeconds: number): string {
    const d = new Date(epochSeconds * 1000);
    return d.toLocaleString([], { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' });
  }

  /* Save / cancel ------------------------------------------------------- */

  onCancel(): void {
    if (this.saving) { return; }
    this.cancel.emit();
  }

  onSave(): void {
    if (this.saving) { return; }
    // Drop empty groups, then gates left with no non-empty group.
    const rules: GateRule[] = this.gates
      .map((gate) => ({
        gate: gate.gate,
        groups: gate.groups
          .map((grp) => grp.rules.map((dr) => dr.rule))
          .filter((rules) => rules.length > 0)
      }))
      .filter((gateRule) => gateRule.groups.length > 0);
    this.save.emit({ rules, seedRequests: this.collectSeedRequests(rules) });
  }

  // Flattens Section & Row / Ticket Usage / Price Code rules into one seed
  // request per concrete locator, carrying the rule's stable seedKey. A locator
  // rule seeds the same test row regardless of which OR group it sits in, so we
  // de-duplicate across groups by seedKey + locator.
  private collectSeedRequests(rules: GateRule[]): SeedRowRequest[] {
    const requests: SeedRowRequest[] = [];
    const seen = new Set<string>();
    const push = (req: SeedRowRequest): void => {
      const hint = req.locatorHint;
      const dedupeKey = [req.gate, req.seedKey, hint.section ?? '', hint.row ?? '',
        hint.priceCode ?? '', hint.ticketUsageLabel ?? ''].join('|');
      if (seen.has(dedupeKey)) { return; }
      seen.add(dedupeKey);
      requests.push(req);
    };
    for (const gateRule of rules) {
      for (const rule of gateRule.groups.flat()) {
        if (rule.type === 'section_row') {
          for (const sel of rule.selections) {
            const rows = sel.rows.length ? sel.rows : [''];
            for (const row of rows) {
              push({
                gate: gateRule.gate,
                ruleType: 'section_row',
                seedKey: rule.seedKey,
                locatorHint: { section: sel.section, row }
              });
            }
          }
        } else if (rule.type === 'ticket_usage') {
          push({
            gate: gateRule.gate,
            ruleType: 'ticket_usage',
            seedKey: rule.seedKey,
            locatorHint: { ticketUsageLabel: `${rule.dayUsage}/${rule.entryUsage}` }
          });
        } else if (rule.type === 'price_code') {
          for (const code of rule.priceCodes) {
            push({
              gate: gateRule.gate,
              ruleType: 'price_code',
              seedKey: rule.seedKey,
              locatorHint: { priceCode: code }
            });
          }
        }
      }
    }
    return requests;
  }

  /* trackBy -------------------------------------------------------------- */
  // trackBy functions must not use `this` — Angular calls them without context.
  trackGate(_idx: number, gate: DraftGate): string { return gate.gate; }
  trackGroup(_idx: number, group: DraftGroup): string { return group.id; }
  trackDraftRule(_idx: number, dr: DraftRule): string { return dr.id; }
  trackRuleType(_idx: number, type: EntryRule['type']): string { return type; }
}
